// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { CORRECTION_STATUSES } from '../enums.js';
import { booleanQuerySchema, cursorQuerySchema, isoDateSchema, isoDateTimeSchema, paginationQuerySchema, uuidSchema } from '../common.js';
import { dailyAttendanceQuerySchema, monthlyAttendanceQuerySchema } from '../attendance.js';
export const dailyAttendanceListQuerySchema = dailyAttendanceQuerySchema.extend(paginationQuerySchema.shape);
/**
 * POST /orgs/:orgId/attendance/daily/export — the daily register's CURRENT view (date, branch, department, shift, status, flag,
 * search) as a spreadsheet, generated by the worker as a `daily_attendance` report (AGENTS.md rule 5: 202 with the report request
 * and queue job ids). The file is downloaded from the Reports page (report.export, owner-only signed URL, audited with its row count).
 */
export const dailyRegisterExportSchema = dailyAttendanceQuerySchema.extend({
    format: z.enum(['xlsx', 'csv']).default('xlsx'),
    /** Required when the organisation asks for a reason on exports (Settings → Security). */
    reason: z.string().trim().max(500).optional(),
});
/** Records one register export may hold; above it the export asks for a branch / department filter. */
export const DAILY_REGISTER_EXPORT_MAX_ROWS = 20_000;
export const monthlyAttendanceListQuerySchema = monthlyAttendanceQuerySchema.extend({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(100).optional(),
});
export const RAW_PROCESSING_STATUSES = ['pending', 'normalized', 'unmatched', 'ignored', 'error', 'quarantined', 'held'];
export const rawTransactionsQuerySchema = cursorQuerySchema.extend({
    deviceId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
    processingStatus: z.enum(RAW_PROCESSING_STATUSES).optional(),
    deviceEmployeeId: z.string().max(64).optional(),
    employeeId: uuidSchema.optional(),
    /** `mapped` = attributed to an employee; `unmapped` = not (yet) attributed (pending, unmatched, ignored, …). */
    mapping: z.enum(['mapped', 'unmapped']).optional(),
});
export const correctionListQuerySchema = paginationQuerySchema.extend({
    status: z.enum(CORRECTION_STATUSES).optional(),
    employeeId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
});
export const correctionCancelSchema = z.object({ reason: z.string().max(500).optional() });
export const periodUnlockSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export const periodLockListQuerySchema = z.object({ branchId: uuidSchema.optional(), includeUnlocked: booleanQuerySchema.default(false), year: z.coerce.number().int().min(2000).max(2100).optional() });
export const recalculationListQuerySchema = paginationQuerySchema.extend({ status: z.enum(['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED']).optional() });
//# sourceMappingURL=attendance.js.map